export const TABS = {
  MAIN: "main",
  SUMMARIES: "summaries",
  // HELP and SETTINGS occupy the same tab position. SETTINGS replaces HELP for users
  // in the verbose_summaries feature flag. HELP will be removed once SETTINGS is fully rolled out.
  HELP: "help",
  SETTINGS: "settings",
}

export const AVAILABILITY_TIMEOUT_ERROR = "AVAILABILITY_TIMEOUT";

// Constants representing different AiMS availability and behavior statuses
// /preferences GET is aborted after this long and treated as a load failure.
export const PREFERENCES_TIMEOUT_MS = 20000;

// zoomSdk.getRunningContext() values.
export const ZOOM_RUNNING_CONTEXT = {
  IN_MEETING: "inMeeting",
  IN_MAIN_CLIENT: "inMainClient",
};

// RTMS start timings shared by the summary/transcribe (home) and HIRE controllers.
export const RTMS_START_TIMING_MS = {
  STATUS_POLL: 5000, // session-status poll while a start is pending
  START_TIMEOUT: 15000, // give up on a start that hasn't gone active
  RESTART_START_TIMEOUT: 60000, // same, for a restart attempt
  // The first attempt already waited 15s; wait 45s more so the backend's 60s start timeout has passed.
  RESTART_DELAY: 45000,
  REJECTED_RESET: 120000, // reset a rejected start if no "started" event arrives
};

export const AIMS_STATUS = {
  // AiMS is available for this meeting, both the organizer and attendees will receive the summary
  AVAILABLE: "AimsAvailable",

  // AiMS is partially available, only the organizer will receive the summary
  SUMMARY_TO_ORGANIZER: "SummaryToOrganizer",

  // AiMS is not available for this meeting (e.g., due to legal policy)
  NOT_AVAILABLE: "AimsNotAvailable",

  // The meeting has external attendee
  EXTERNAL_ATTENDEE: "ExternalAttendee",

  // AiMS failed due to an unexpected error (could be from frontend or backend)
  EXCEPTION: "AimsException",

  // AiMS timed out due to some downstream dependency taking too long (ex - availability lambda taking longer than 30 seconds)
  TIMEOUT: "AimsTimeout",

  // Transcript is available for this meeting
  TRANSCRIPT_AVAILABLE: "TranscriptAvailable",

  // Transcript is not available for this meeting
  TRANSCRIPT_NOT_AVAILABLE: "TranscriptNotAvailable",
};

export const AIMS_NOT_AVAILABLE_TITLE = "Inform attendees if needed";

export const AIMS_NOT_AVAILABLE_MESSAGE = {
  DEFAULT: "Not all meetings are approved for Amazon Meetings.",
  REGIONS: "Some attendees are from regions where Amazon Meetings isn't yet available.",
};

export const TRANSCRIPT_NOT_AVAILABLE_MESSAGE = "Some participants are from regions where transcription isn't yet available.";

export const AIMS_AVAILABILITY_MESSAGE_TYPES = {
  WORK_COUNCIL: "work_council",
  HIRE_LEO: "hire-leo",
  HIRE: "hire",
};

// Which AiMS flow a meeting goes through. See resolveMeetingMode in utils/meetingInitUtils.js.
export const AIMS_FLOWS = {
  SUMMARY: "summary",
  TRANSCRIPT: "transcript",
  HIRE: "hire",
};

// Width of the in-meeting app panel.
export const PANEL_WIDTH = "362px";

// Amazon's Recording, Transcription, and Summarization Policy, and the AiMS wiki (+ its privacy section).
export const RTS_POLICY_URL = "https://policy.a2z.com/docs/693659/publication";
export const AIMS_WIKI_URL = "https://w.amazon.com/bin/view/AmazonMeetings/MeetingSummaries/AiMS/";
export const AIMS_PRIVACY_URL = `${AIMS_WIKI_URL}#HPrivacy`;

// Zoom in-meeting chat disclaimer posted when summarization auto-starts or this user starts transcription.
export const START_DISCLAIMER_CHAT_MESSAGE = `For Amazon meeting organizers:
Review Amazon's Recording, Transcription, and Summarization Policy for permitted meeting types.
${RTS_POLICY_URL}

Notify attendees when meetings are being summarized or transcribed.

Meeting organizers are responsible for compliance. Refer to wiki for guidelines.
${AIMS_WIKI_URL}`;

// Start / End button width on status screens.
export const STATUS_BUTTON_MIN_WIDTH = "194px";

// WaveformLayout icon circle: warning / error icons scaled from Meridian's 24px to 32px.
export const WAVEFORM_ICON_SCALE = 1.33;

export const ROLES = {
  ORGANIZER: "organizer",
  ALTERNATIVE_HOST: "alternativeHost",
  ATTENDEE: "attendee",
};

export const SUMMARIZE_STATES = {
  DEFAULT: 'default',           // Ready to summarization
  LOADING: 'loading',           // Starting/stopping summarization
  SUMMARIZING: 'summarizing',   // Currently summarizing
  REJECTED: 'rejected',         // Someone else is starting
  STOPPED: 'stopped',           // Summarization stopped
  READY: 'ready'                // Summary is ready
};

export const TRANSCRIPTION_STATES = {
  DEFAULT: 'default',           // Ready to transcription
  LOADING: 'loading',           // Starting/stopping transcription
  TRANSCRIBING: 'transcribing', // Currently transcribing
  REJECTED: 'rejected',         // Someone else is starting
  STOPPED: 'stopped',           // Transcription stopped
};

export const OUTLOOK_WARNINGS = {
  MULTI_MEETING_FOUND_FROM_OUTLOOK: "MultiMeetingFoundFromOutlook",
  MEETING_NOT_FOUND_FROM_OUTLOOK: "MeetingNotFoundFromOutlook"
};

export const RTMS_REQUESTER_DEFAULT_NAME = "Meeting organizer";

// Context constants for modals and components
export const MAIN_CONTEXT = {
  SUMMARIZE: "summarize",
  TRANSCRIBE: "transcribe",
};

// SessionStorage keys for summary pagination
export const SESSION_STORAGE_KEYS = {
  SUMMARY_CACHE: "summaryCache",
  LAST_EVALUATED_KEY: "lastEvaluatedKey",
  RAW_SUMMARIES: "rawSummaries",
  IS_END_OF_DATA: "isEndOfData",
};

// Feature flags
export const FEATURE_FLAGS = {
  ALLOW_SUMMARY_DISTRIBUTION_TOGGLE: true, // Disabled pending legal review on allowing organizers to restrict summary distribution
};

// Autostart modes for transcription
export const AUTO_START_MODES = {
  SINGLE: "single",
  MULTIPLE: "multiple",
};

export const VERBOSITY_LEVELS = {
  CONCISE: "concise",
  MODERATE: "moderate",
  DETAILED: "detailed",
};

// --- Feedback / prompt / Whos Who constants (merged from components/common/constants.jsx) ---

export const FEEDBACK_PAGE = {
    SUMMARIZE_END: "summarizeEnd",
    IN_MEETING_FEEDBACK_TAB: "inMeetingFeedbackTab", 
    IN_CLIENT_FEEDBACK_TAB: "inClientFeedbackTab"
}

export const FEEDBACK = {
    THUMBS_UP: "ThumbsUp",
    THUMBS_DOWN: "ThumbsDown"
};

// PhoneTool profile page for an Amazon alias; append the alias.
export const PHONE_TOOL_USER_BASE = "https://phonetool.amazon.com/users/";


// --- Summary classification constants (merged from components/common/summary/constants.jsx) ---

export const SUMMARY_FEEDBACK_STATUS = {
  APPROVED: 'approved',
  NOT_APPROVED: 'not_approved',
  OTHER: 'other'
}

export const SUMMARY_TYPES = {
  APPROVED: ["low_risk"],
  NOT_APPROVED: ["performance_review", "hr_complaint", "investigation", "interview"],
};

// In-session content tabs (the primary UI's session view). Distinct from TABS above,
// which is the legacy nav.
export const SESSION_TABS = {
  ASSISTANT: "assistant",
  AGENDA: "agenda",
  ACTION_ITEMS: "actionItems",
};

// View states for the main container
export const VIEWS = {
  MAIN: "main",
  SUMMARIES: "summaries",
  SUMMARY_DETAIL: "summaryDetail",
  SETTINGS: "settings",
};


// Built-in agent — always present, always enabled, not part of the extensions platform.
export const BUILT_IN_AGENT = {
  id: "amazon-meetings",
  name: "Amazon Meetings Assistant",
  shortName: "ama",
};

// Shown as a toast when saving a user preference (auto-start or summary detail
// level) fails — i.e. the /preferences update did not return HTTP 200.
export const PREFERENCE_SAVE_ERROR_MESSAGE =
  "Couldn't save your changes right now. Try again in a moment.";

// Box fill radius large enough to fully round a pill-shaped badge at any height.
export const PILL_RADIUS_FILL = 999;
